import { useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { DownloadIcon, EllipsisIcon, InfoIcon } from "@lucide/icons";

import { formatDate } from "simplydo/core/dates";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Pagination } from "simplydo/react/ui/pagination";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import util from "utils/utils";

import type {
  IdeaCollaboratorUser,
  IdeaCopySuggestion,
  UpdateIdeaFn,
} from "components/ideas/types";
import { MultiLineParagraph } from "components/lib/UI";

import type { IdeaTemplateField } from "./Body/Custom/FieldProps";

const Warn = styled.div`
  background-color: #ff1234;
  padding: 2px;
  border-radius: 50%;

  &&& img {
    margin-right: 0;
  }
`;

/* after content renders as non-breaking space + asterisk */
const StyledH2 = styled.h2<{ $isRequired: boolean }>`
  margin-bottom: 0;
  &::after {
    display: ${({ $isRequired }) => ($isRequired ? "inline" : "none")};
    content: "\\00a0*";
    color: #cd0e2b;
    font-size: 1.2em;
  }
`;

export const StyledHeader = styled.header`
  position: relative;

  span {
    margin: 0;
    display: block;
    font-weight: 350;
  }
  h2 {
    line-height: 21px;
    font-size: 15px;
    font-weight: 600;
    color: rgb(40, 40, 40);
  }
  p {
    margin-top: 0px;
    margin-bottom: 15px;
    font-size: 13px;
    color: #2a2828;
    padding: 3px;
    padding-left: 6px;
    border-left: 4px solid #ffe4b5;
    background-color: #fffad1;
    display: block;
  }
`;

const TitleRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 5px;

  min-height: 36px;

  align-items: center;
  justify-content: space-between;
`;

const ClippedTextStyle = styled.div`
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  align-content: center;
  word-break: break-word;
`;

const ClippedText = ({ children }: { children?: React.ReactNode }) =>
  children ? (
    <Popup
      content={children}
      trigger={<ClippedTextStyle>{children}</ClippedTextStyle>}
    />
  ) : null;

type FieldHeaderProps = {
  ideaId: string;
  field: IdeaTemplateField;
  externalChange?:
    | Record<string, IdeaCollaboratorUser>
    | IdeaCollaboratorUser[];
  updateIdea: UpdateIdeaFn;
  importingContentEnabled: boolean;
  showDescription?: boolean;
  showRequiredStatus?: boolean;
  showImportContent?: boolean;
};

const editorsFromExternalChange = (
  externalChange:
    | Record<string, IdeaCollaboratorUser>
    | IdeaCollaboratorUser[]
    | undefined,
): IdeaCollaboratorUser[] => {
  if (!externalChange) return [];
  const values = Array.isArray(externalChange)
    ? externalChange
    : Object.values(externalChange);
  return values.filter(
    (entry): entry is IdeaCollaboratorUser =>
      !!entry && typeof entry === "object" && typeof entry._id === "string",
  );
};

const FieldHeader = ({
  ideaId,
  field,
  externalChange,
  updateIdea,
  importingContentEnabled,
  showDescription,
  showRequiredStatus,
  showImportContent,
}: FieldHeaderProps) => {
  const { t } = useTranslation();
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsOpen, setSuggestionOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<IdeaCopySuggestion[]>([]);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [suggestionQuery, setSuggestionQuery] = useState("");

  const { title, description = "", required, type } = field;
  const editingUsers = useMemo(
    () => editorsFromExternalChange(externalChange),
    [externalChange],
  );

  const paginatedSuggestions = useMemo(() => {
    return suggestions.slice((suggestionPage - 1) * 5, suggestionPage * 5);
  }, [suggestions, suggestionPage]);

  const loadSuggestions = useThrottle(
    (query?: string) => {
      api.ideas.getCopySuggestions(
        ideaId,
        type,
        // OpenAPI omits optional query params at runtime
        (query ? { query } : undefined) as Parameters<
          typeof api.ideas.getCopySuggestions
        >[2],
        (res) => {
          setSuggestionOpen(true);
          setSuggestionPage(1);
          setLoadingSuggestions(false);
          setSuggestions(res.suggestions);
        },
        (err) => {
          if (err.status === 400) {
            toast("No support for importing this field type yet.");
          }
          setLoadingSuggestions(false);
        },
      );
    },
    300,
    [type, ideaId],
  );

  return (
    <StyledHeader>
      <TitleRow>
        <StyledH2 $isRequired={!!(showRequiredStatus && required)}>
          {title}
        </StyledH2>
        <a
          id={"field-" + field.id}
          style={{ position: "absolute", top: -200, left: 0 }}
        ></a>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            marginTop: -6,
            marginLeft: 12,
            height: 24,
          }}
        >
          {editingUsers.map((u) => {
            const displayName = u.profile?.fullName || u.name || "Unknown user";
            return (
              <Popup
                key={u._id}
                trigger={
                  <Warn>
                    <ImageWithFallback
                      avatar
                      src={util.avatarUrl(u)}
                      fallbackSrc={util.avatarUrl()}
                      data-tooltip={displayName}
                    />
                  </Warn>
                }
                content={
                  <>
                    <H4>
                      Warning
                      <Subheader>
                        {`${displayName} is currently editing this field, to ensure that no work is lost please wait until they are done.`}
                      </Subheader>
                    </H4>
                  </>
                }
              />
            );
          })}
        </div>
        {showImportContent &&
        importingContentEnabled &&
        [
          "shorttext",
          "longtext",
          "richtext",
          "numerictext",
          "creditsafereport",
          "slider",
          "image",
          "multiimage",
          "file",
          "multifile",
        ].includes(field.type) ? (
          <>
            <Dropdown
              upward
              direction="left"
              tabIndex={-1}
              trigger={
                <Button
                  tabIndex={-1}
                  style={{ marginLeft: 10 }}
                  size="small"
                  onClick={() => {}}
                  variant="outline"
                >
                  <Icon icon={EllipsisIcon} />
                </Button>
              }
            >
              <DropdownMenu>
                <DropdownItem
                  onClick={() => {
                    setLoadingSuggestions(true);
                    loadSuggestions();
                  }}
                >
                  <Icon icon={DownloadIcon} />
                  Import content
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Modal open={suggestionsOpen}>
              <ModalHeader>
                Import content from another {t("generic.idea")}
              </ModalHeader>
              <ModalContent>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                    gap: 10,
                  }}
                >
                  <H4>Choose a field to import from</H4>
                  <p>
                    Select content from a field you have previously completed in
                    another {t("generic.idea")}. Just click one of the fields to
                    import its content. This will replace the current content in
                    this field.
                  </p>
                  <Input
                    loading={loadingSuggestions}

                    placeholder="Search..."
                    value={suggestionQuery}
                    onChange={(e) => {
                      setLoadingSuggestions(true);
                      setSuggestionQuery(e.target.value);
                      loadSuggestions(e.target.value);
                    }}
                  />
                  {suggestions.length === 0 ? (
                    <Message color="blue" style={{ width: "100%" }}>
                      <Icon icon={InfoIcon} />
                      <MessageHeader>No content found</MessageHeader>
                      <MessageContent>
                        {suggestionQuery ? (
                          <p>No content matching your query could be found.</p>
                        ) : (
                          <p>
                            You haven&apos;t created content for a type of this
                            field before.
                          </p>
                        )}
                      </MessageContent>
                    </Message>
                  ) : (
                    <>
                      <Table selectable verticalAlign="middle">
                        <TableHeader>
                          <TableRow>
                            <TableHeaderCell collapsing></TableHeaderCell>
                            <TableHeaderCell>
                              {t("common:capitalise", {
                                key: "generic.challenge",
                              })}
                            </TableHeaderCell>
                            <TableHeaderCell>
                              {t("common:capitalise", { key: "generic.idea" })}
                            </TableHeaderCell>
                            <TableHeaderCell>Field</TableHeaderCell>
                            <TableHeaderCell>Preview</TableHeaderCell>
                            <TableHeaderCell>Used on</TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedSuggestions.map((s) => (
                            <TableRow
                              key={s.summary}
                              onClick={() => {
                                util
                                  .confirm(
                                    `Import ${t("generic.idea")} data`,
                                    `Are you sure you want to import the content of this field? This will remove and overwrite the content in the field of the ${t("generic.idea")} you are currently editing!`,
                                  )
                                  .then(() => {
                                    api.ideas.copyData(
                                      s.idea._id,
                                      s.fieldId,
                                      ideaId,
                                      field.id,
                                      // OpenAPI omits optional index query param
                                      (s.preview?.index != null
                                        ? { index: s.preview.index }
                                        : undefined) as Parameters<
                                        typeof api.ideas.copyData
                                      >[4],
                                      ({ content }) => {
                                        updateIdea(
                                          content,
                                          ["templated", field.id],
                                          true,
                                        );
                                        setSuggestionOpen(false);
                                      },
                                      () => {
                                        toast.error("Failed to import content");
                                      },
                                    );
                                  })
                                  .catch(() => {});
                              }}
                            >
                              <TableCell collapsing width="1">
                                <ImageWithFallback
                                  avatar
                                  style={{
                                    objectFit: "cover",
                                    borderRadius: 4,
                                  }}
                                  src={
                                    s.idea.coverImageThumbnailUrls?.[0] ??
                                    s.idea.coverImageUrl ??
                                    "images/idea.png"
                                  }
                                  fallbackSrc={util.ideaCoverImage(
                                    undefined,
                                    "200x200",
                                  )}
                                />
                              </TableCell>
                              <TableCell width="2">
                                <ClippedText>
                                  {s.idea.ownerChallenge.name}
                                </ClippedText>
                              </TableCell>
                              <TableCell width="2">
                                <ClippedText>{s.idea.name}</ClippedText>
                              </TableCell>
                              <TableCell width="2">
                                <ClippedText>{s.fieldName}</ClippedText>
                              </TableCell>
                              <TableCell width="5">
                                <ClippedText>
                                  {s.preview.url ? (
                                    s.preview.type === "image" ? (
                                      <Image
                                        src={s.preview.url}
                                        style={{
                                          maxWidth: "100%",
                                          maxHeight: "100%",
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) : (
                                      <Label>{s.preview.name}</Label>
                                    )
                                  ) : (
                                    s.preview.summary
                                  )}
                                </ClippedText>
                              </TableCell>
                              <TableCell width="2">
                                <ClippedText>
                                  {formatDate(s.idea.updatedAt, "dd/MM/yyyy")}
                                </ClippedText>
                              </TableCell>
                              <TableCell collapsing>
                                <Button size="small" color="secondary">
                                  <Icon icon={DownloadIcon} />
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Pagination
                        style={{ alignSelf: "center" }}
                        activePage={suggestionPage}
                        totalPages={Math.ceil(suggestions.length / 5)}
                        onPageChange={(_e, { activePage }) =>
                          setSuggestionPage(activePage as number)
                        }
                      />
                    </>
                  )}
                </div>
              </ModalContent>
              <ModalActions>
                <Button
                  onClick={() => {
                    setSuggestionOpen(false);
                  }}
                >
                  Close
                </Button>
              </ModalActions>
            </Modal>
          </>
        ) : null}
      </TitleRow>
      {showDescription && description.trim() && (
        <MultiLineParagraph>
          <Icon icon={InfoIcon} style={{ marginRight: 5 }} />
          {description}
        </MultiLineParagraph>
      )}
    </StyledHeader>
  );
};

export default FieldHeader;
